import { Glue42 } from "../../glue";
import { default as CallbackRegistryFactory, CallbackRegistry } from "callback-registry";
import { isFunction, isObject, isString } from "../activity/helpers/util";

export interface AppPreferences {
    app: string;
    data: any;
    lastUpdate: Date;
}

export class Prefs {

    private registry: CallbackRegistry = CallbackRegistryFactory();
    private interopMethodRegistered = false;

    constructor(private appName: string, private interop: Glue42.Interop.API) {
    }

    public async get(app?: string): Promise<AppPreferences | undefined> {
        const data = (await this.interop.invoke("T42.Prefs.Get", { app: app ?? this.appName }));
        return data.returned;
    }

    public async set(data: any, options?: { app: string }): Promise<void> {
        this.verifyDataObject(data);
        await this.interop.invoke("T42.Prefs.Set", { app: options?.app ?? this.appName, data, merge: false });
    }

    public async setFor(app: string, data: any): Promise<void> {
        this.verifyApp(app);
        this.verifyDataObject(data);
        return this.set(data, { app });
    }

    public async update(data: any, options?: { app: string }) {
        this.verifyDataObject(data);
        await this.interop.invoke("T42.Prefs.Set", { app: options?.app ?? this.appName, data, merge: true });
    }

    public async updateFor(app: string, data: any) {
        this.verifyApp(app);
        this.verifyDataObject(data);
        return this.update(data, { app });
    }

    public async clear(app?: string) {
        await this.interop.invoke("T42.Prefs.Set", { app: app ?? this.appName, clear: true });
    }

    public async clearFor(app: string) {
        this.verifyApp(app);
        await this.interop.invoke("T42.Prefs.Set", { app, clear: true });
    }

    public async getAll(): Promise<AppPreferences[]> {
        const data = (await this.interop.invoke("T42.Prefs.Get"));
        return data.returned;
    }

    public async clearAll() {
        await this.interop.invoke("T42.Prefs.Set", { clear: true });
    }

    public subscribe(callback: (prefs: AppPreferences) => void): () => void {
        this.verifyCallback(callback);
        return this.subscribeFor(this.appName, callback);
    }

    public subscribeFor(app: string, callback: (prefs: AppPreferences) => void): () => void {
        this.verifyApp(app);
        this.verifyCallback(callback);
        const unsubscribeFn = this.registry.add(app, callback);

        // register the method if needed and then send the subscription request
        this.registerInteropIfNeeded()
            .then(() => {
                this.interop.invoke("T42.Prefs.Get", { app, subscribe: true });
            });

        return () => {
            unsubscribeFn();
        };
    }

    private async registerInteropIfNeeded() {
        if (this.interopMethodRegistered) {
            return;
        }
        this.interopMethodRegistered = true;

        await this.interop.register("T42.Prefs.Update", (args) => {
            this.registry.execute(args.app, args);
        });
    }

    private verifyApp(app: string) {
        if (!app) {
            throw new Error(`app should be defined`);
        }
        if (!isString(app)) {
            throw new Error(`app should be a string`);
        }
    }
    private verifyDataObject(data: any) {
        if (!data) {
            throw new Error(`data should be defined`);
        }
        if (!isObject(data)) {
            throw new Error(`data should be an object`);
        }
    }

    private verifyCallback(callback: any) {
        if (!isFunction(callback)) {
            throw new Error(`callback should be defined`);
        }
    }
}
